<template>
  <div class="my-addresses-container responsive-p">
    <div class="top-header-wrapper">
      <button @click="back" v-if="!isDesktop">
        <img src="@/assets/icons/primary-back.svg" alt="back">
      </button>
      <p class="title">{{ t('myAddresses.changeTitle') }}</p>
    </div>
    <div class="no-item-wrapper" v-if="addresses.length === 0 && !showForm">
      <img src="@/assets/images/address.svg" alt="address">
      <p class="no-item-text">
        {{  t('myAddresses.noItem') }}
      </p>
    </div>
    <div class="items-wrapper" v-if="addresses.length > 0">
      <AddressCard
        v-for="address in addresses"
        :key="address.id"
        :address="address"
        hiddenRemove
        :isSelected="address.isSelected"
      />
    </div>

    <div class="add-section">
      <button class="add-toggle" type="button" @click="toggleForm">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        เพิ่มที่อยู่จัดส่งใหม่
      </button>
    </div>

    <MButton @click="back" class="fixed-button">{{ t('submit') }}</MButton>

    <!-- Modal popup -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showForm" @click.self="cancelForm">
        <div class="modal-sheet">
          <div class="modal-header">
            <span class="modal-title">เพิ่มที่อยู่จัดส่งใหม่</span>
            <button class="modal-close" type="button" @click="cancelForm" aria-label="ปิด">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="modal-body">
            <TextInput v-model="receiveName" :label="t('addAddresses.form.receiveName')" required />
            <TextInput v-model="phone" :label="t('addAddresses.form.phone')" required />
            <DropdownInput
              :label="t('addAddresses.form.address')"
              :items="addressTypeChoices"
              v-model="addressType"
              required
            />
            <TextInput v-model="addressNo" :label="t('addAddresses.form.addressNo')" required />
            <DropdownInput
              :label="t('addAddresses.form.province')"
              :items="provinceChoices"
              v-model="province"
              required
            />
            <DropdownInput
              :label="t('addAddresses.form.district')"
              :items="districtChoices"
              v-model="district"
              required
            />
            <DropdownInput
              :label="t('addAddresses.form.subDistrict')"
              :items="subDistrictChoices"
              v-model="subDistrict"
              required
            />
            <TextInput v-model="zipCode" :label="t('addAddresses.form.zipCode')" required />
            <CheckBox v-model="isDefault" :label="t('addAddresses.form.setMainAddress')" class="mb-2" />
            <div class="form-actions">
              <button class="btn-cancel" type="button" @click="cancelForm">ยกเลิก</button>
              <MButton class="btn-save" @click="saveAddress" :disabled="saving">
                {{ saving ? 'กำลังบันทึก…' : t('addAddresses.form.save') }}
              </MButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { onMounted, computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useScreenSize } from '@/utils/isDesktop'

import AddressCard from '@/components/AddressCard'
import MButton from '@/components/Form/MButton'
import TextInput from '@/components/Form/TextInput'
import DropdownInput from '@/components/Form/DropdownInput'
import CheckBox from '@/components/Form/CheckBox'

export default {
  name: 'MyAddressSelectView',
  components: {
    AddressCard,
    MButton,
    TextInput,
    DropdownInput,
    CheckBox,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const { t, locale } = useI18n()
    const { isDesktop } = useScreenSize()

    /** address list */
    const addresses = computed(() => store.state.user.addresses || [])

    /** form visibility */
    const showForm = ref(false)
    const saving = ref(false)

    /** form fields */
    const receiveName = ref('')
    const phone = ref('')
    const addressType = ref({ value: t('addAddresses.form.home'), label: t('addAddresses.form.home') })
    const addressNo = ref('')
    const province = ref({})
    const district = ref({})
    const subDistrict = ref({})
    const zipCode = ref('')
    const isDefault = ref(false)

    const addressTypeChoices = [
      { value: t('addAddresses.form.home'), label: t('addAddresses.form.home') },
      { value: t('addAddresses.form.work'), label: t('addAddresses.form.work') },
    ]

    const provinceChoices = computed(() => store.state.user.provinces.map(i => ({ ...i, value: i.province_code, label: i.province_name })))
    const districtChoices = computed(() => store.state.user.districts.map(i => ({ ...i, value: i.district_code, label: i.district_name })))
    const subDistrictChoices = computed(() => store.state.user.subDistricts.map(i => ({ ...i, value: i.sub_district_code, label: i.sub_district_name })))

    const resetForm = () => {
      receiveName.value = ''
      phone.value = ''
      addressType.value = { value: t('addAddresses.form.home'), label: t('addAddresses.form.home') }
      addressNo.value = ''
      province.value = {}
      district.value = {}
      subDistrict.value = {}
      zipCode.value = ''
      isDefault.value = false
    }

    const toggleForm = () => {
      showForm.value = !showForm.value
      if (showForm.value) {
        store.dispatch('user/fetchGetProvince')
      }
    }

    const cancelForm = () => {
      showForm.value = false
      resetForm()
    }

    const showWarning = (message) => store.commit('snackBar/SET_DATA', { message, color: 'warning' })

    const saveAddress = async () => {
      const getReq = (label) => locale.value === 'th' ? `กรุณากรอก${label}` : `Please fill ${label}`
      const required = [
        [receiveName.value?.trim(), t('addAddresses.form.receiveName')],
        [phone.value?.trim(), t('addAddresses.form.phone')],
        [addressType.value?.label, t('addAddresses.form.address')],
        [addressNo.value?.trim(), t('addAddresses.form.addressNo')],
        [province.value?.label, t('addAddresses.form.province')],
        [district.value?.label, t('addAddresses.form.district')],
        [subDistrict.value?.label, t('addAddresses.form.subDistrict')],
        [String(zipCode.value || '').trim(), t('addAddresses.form.zipCode')],
      ]
      const missing = required.find(([v]) => !v)
      if (missing) return showWarning(getReq(missing[1]))

      if (!/^(0[689])[0-9]{8}$/.test(String(phone.value).trim())) {
        return showWarning(locale.value === 'th' ? 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง' : 'Please enter a valid phone number')
      }

      saving.value = true
      try {
        await store.dispatch('user/fetchAddNewAddress', {
          name: receiveName.value?.trim(),
          phone: String(phone.value).trim(),
          address_name: addressType.value?.label,
          address_number: addressNo.value?.trim(),
          address_province: province.value?.label,
          address_district: district.value?.label,
          address_sub_district: subDistrict.value?.label,
          address_postcode: String(zipCode.value).trim(),
          is_primary: isDefault.value,
          is_in_country: true,
        })
        store.commit('snackBar/SET_DATA', { message: 'เพิ่มที่อยู่สำเร็จ', color: 'success' })
        showForm.value = false
        resetForm()
        store.dispatch('user/fetchGetAddresses')
      } catch {
        // store already shows error snackbar
      } finally {
        saving.value = false
      }
    }

    const back = () => router.back()

    watch(() => province.value, (val) => {
      if (val?.value) {
        store.dispatch('user/fetchGetDistrict', val.value)
        district.value = {}
        subDistrict.value = {}
        zipCode.value = ''
      }
    })

    watch(() => district.value, (val) => {
      if (val?.value) {
        store.dispatch('user/fetchGetSubDistrict', val.value)
        subDistrict.value = {}
        zipCode.value = ''
      }
    })

    watch(() => subDistrict.value, (val) => {
      if (val?.postcode) zipCode.value = val.postcode
    })

    onMounted(() => {
      store.dispatch('user/fetchGetAddresses')
    })

    return {
      t,
      back,
      addresses,
      isDesktop,
      showForm,
      saving,
      toggleForm,
      cancelForm,
      saveAddress,
      receiveName,
      phone,
      addressType,
      addressTypeChoices,
      addressNo,
      province,
      district,
      subDistrict,
      zipCode,
      isDefault,
      provinceChoices,
      districtChoices,
      subDistrictChoices,
    }
  }
}
</script>

<style lang="scss" scoped>

.my-addresses-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-top: 56px;
  position: relative;
  @media screen and (min-width: 780px) {
    padding-top: 0;
    height: calc(100% - 74px);
  }
}

.top-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-bottom: 1px solid #D6E2EF;
  background-color: var(--white-color);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  @media screen and (min-width: 780px) {
    position: relative;
    border-bottom: 0;
  }
  > button {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: bold;
    font-size: 20px;
    color: var(--secondary-color);
    outline: none;
    background-color: transparent;
    border: 0;
    padding: 8px;
  }
}

.title {
  font-size: 24px;
  font-weight: bold;
}

.no-item-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.items-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 16px 0;
}

.no-item-text {
  font-size: 20px;
  margin-top: 16px;
}

.add-section {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  border: 1.5px dashed var(--primary-color);
  border-radius: 8px;
  background: transparent;
  color: var(--primary-color);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  > svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  &:active {
    background: rgba(var(--primary-color-rgb, 42, 46, 143), 0.05);
  }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  @media screen and (min-width: 780px) {
    align-items: center;
    justify-content: center;
  }
}

.modal-sheet {
  width: 100%;
  max-height: 90vh;
  background: #fff;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 780px) {
    max-width: 520px;
    border-radius: 16px;
    max-height: 85vh;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #D6E2EF;
  flex-shrink: 0;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
}

.modal-close {
  background: none;
  border: 0;
  padding: 4px;
  cursor: pointer;
  color: #888;
  display: flex;
  align-items: center;
  > svg { width: 20px; height: 20px; }
}

.modal-body {
  overflow-y: auto;
  padding: 16px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.btn-cancel {
  flex: 1;
  padding: 12px;
  border: 1px solid #C3C3C3;
  border-radius: 8px;
  background: #fff;
  color: var(--secondary-color);
  font-size: 16px;
  cursor: pointer;
}

.btn-save {
  flex: 2;
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.25s ease;
  .modal-sheet {
    transition: transform 0.25s ease;
  }
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
  .modal-sheet {
    transform: translateY(100%);
  }
}
@media screen and (min-width: 780px) {
  .modal-enter-from, .modal-leave-to {
    .modal-sheet {
      transform: scale(0.95);
    }
  }
}

.fixed-button {
  position: sticky;
  bottom: 8px;
  margin: 0 8px 8px;
  flex-shrink: 0;
  z-index: 5;
}
</style>
